import { AnalyticsProvider } from './base';
import type { UserProperties, PageViewData, PentictonEventsTracking } from '../types';

declare global {
  interface Window {
    gtag: (...args: any[]) => void;
    dataLayer: any[];
  }
}

export class GoogleAnalyticsProvider extends AnalyticsProvider {
  private trackingId: string;
  private initialized: boolean = false;

  constructor(trackingId: string, enabled: boolean = true, debug: boolean = false) {
    super(enabled, debug);
    this.trackingId = trackingId;
  }

  async initialize(): Promise<void> {
    if (this.initialized || !this.trackingId) {
      return;
    }

    try {
      // Initialize dataLayer
      window.dataLayer = window.dataLayer || [];
      window.gtag = function gtag(...args: any[]) {
        window.dataLayer.push(args);
      };

      // Load GA4 script
      const script = document.createElement('script');
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${this.trackingId}`;
      document.head.appendChild(script);

      // Configure GA4
      window.gtag('js', new Date());
      window.gtag('config', this.trackingId, {
        debug_mode: this.debug,
        send_page_view: false, // We'll handle page views manually
        cookie_flags: 'SameSite=Strict;Secure',
        anonymize_ip: true,
        allow_google_signals: this.consentGranted,
        allow_ad_personalization_signals: this.consentGranted,
      });

      this.initialized = true;
      this.log('Google Analytics initialized', { trackingId: this.trackingId });
    } catch (error) {
      console.error('Failed to initialize Google Analytics:', error);
    }
  }

  trackEvent<T extends keyof PentictonEventsTracking>(
    eventName: T,
    properties: PentictonEventsTracking[T],
  ): void {
    if (!this.shouldTrack() || !this.initialized) {
      this.log('Event tracking skipped', { eventName, properties });
      return;
    }

    try {
      // Map our event structure to GA4 format
      const eventParams = this.mapEventToGA4(eventName, properties);

      window.gtag('event', eventName as string, eventParams);
      this.log('Event tracked', { eventName, properties, eventParams });
    } catch (error) {
      console.error('Failed to track event:', error);
    }
  }

  trackPageView(data: PageViewData): void {
    if (!this.shouldTrack() || !this.initialized) {
      this.log('Page view tracking skipped', data);
      return;
    }

    try {
      window.gtag('event', 'page_view', {
        page_title: data.page_title,
        page_location: data.page_location,
        user_id: data.user_id,
      });

      this.log('Page view tracked', data);
    } catch (error) {
      console.error('Failed to track page view:', error);
    }
  }

  setUserProperties(properties: UserProperties): void {
    if (!this.shouldTrack() || !this.initialized) {
      this.log('User properties setting skipped', properties);
      return;
    }

    try {
      window.gtag('config', this.trackingId, {
        user_id: properties.userId,
        custom_map: {
          role: properties.role,
          membership_type: properties.membershipType,
          join_date: properties.joinDate,
        },
      });

      this.log('User properties set', properties);
    } catch (error) {
      console.error('Failed to set user properties:', error);
    }
  }

  setConsent(granted: boolean): void {
    this.consentGranted = granted;

    if (this.initialized) {
      try {
        window.gtag('consent', 'update', {
          analytics_storage: granted ? 'granted' : 'denied',
          ad_storage: granted ? 'granted' : 'denied',
          ad_user_data: granted ? 'granted' : 'denied',
          ad_personalization: granted ? 'granted' : 'denied',
        });

        this.log('Consent updated', { granted });
      } catch (error) {
        console.error('Failed to update consent:', error);
      }
    }
  }

  private mapEventToGA4(eventName: string, properties: any): Record<string, any> {
    const baseParams: Record<string, any> = {
      event_category: this.getEventCategory(eventName),
      ...properties,
    };

    // Handle specific event mappings
    switch (eventName) {
      case 'rsvp_meeting':
        return {
          ...baseParams,
          currency: 'CAD',
          value: 1, // Base conversion value
          meeting_id: properties.meeting_id,
          group_id: properties.group_id,
        };

      case 'join_group':
        return {
          ...baseParams,
          currency: 'CAD',
          value: 5, // Higher value for group membership
          group_id: properties.group_id,
        };

      case 'first_rsvp':
        return {
          ...baseParams,
          currency: 'CAD',
          value: 10, // High value for first conversion
        };

      default:
        return baseParams;
    }
  }

  private getEventCategory(eventName: string): string {
    if (eventName.includes('rsvp') || eventName.includes('meeting')) {
      return 'Meeting Engagement';
    }
    if (eventName.includes('group')) {
      return 'Group Engagement';
    }
    if (eventName.includes('sign_')) {
      return 'Authentication';
    }
    if (eventName.includes('search') || eventName.includes('filter')) {
      return 'Discovery';
    }
    if (eventName.includes('share')) {
      return 'Social Sharing';
    }
    if (eventName.includes('broadcast') || eventName.includes('create_')) {
      return 'Organizer Actions';
    }
    return 'General';
  }
}
